/**
 * Archivo: MenusComedor.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-07-10
 * 
 * Descripción:
 * Este componente define la vista para la gestión de los menús del comedor. Permite a los usuarios con permisos adecuados
 * visualizar el menú actual y los menús pasados, descargar archivos PDF de los menús y añadir o modificar menús.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^3.0.1
 * - Servicios/MenusComedorServicios: Funciones para interactuar con el servicio de menús.
 * - Componentes/MetaTags: Componente para las etiquetas meta del documento.
 * - Componentes/Cabecera: Componente para la cabecera de la aplicación.
 * - Componentes/SegundaCabecera: Componente para la segunda cabecera de la aplicación.
 * - Componentes/VentanaAñadirMenuComedor: Componente para añadir un nuevo menú.
 * - Componentes/VentanaModificarMenuComedor: Componente para modificar un menú existente.
 * 
 * Propósito:
 * Proveer una interfaz de usuario para la visualización y gestión de los menús del comedor, incluyendo la capacidad
 * de añadir nuevos menús, modificar menús existentes y descargar archivos PDF de los menús.
 */

import { default as React, useState, useEffect } from 'react';
import '../Estilos/estilos.css';
import MetaTags from '../Componentes/MetaTags';
import Cabecera from '../Componentes/Cabecera';
import Cookies from 'js-cookie';
import SegundaCabecera from '../Componentes/SegundaCabecera';
import { listaMenus, descargarMenuPDF } from '../Servicios/MenusComedorServicios';
import VentanaAñadirMenu from '../Componentes/VentanaAñadirMenuComedor';
import VentanaModificarMenu from '../Componentes/VentanaModificarMenuComedor';

import lapizNegro from '../Imagenes/Iconos/LapizNegro.png';

function MenusComedor() {
  const urlMenus = `${process.env.REACT_APP_URL_IMAGENES}MenusComedor/`
  const [menus, setMenus] = useState([]);
  const [menuActual, setMenuActual] = useState(null);
  const [token, setToken] = useState('');
  const [permisos, setPermisos] = useState(null);
  const [hayVentanas, setHayVentanas] = useState(false);
  const [mostrarAñadirMenu, setMostrarAñadirMenu] = useState(false);
  const [mostrarModificarMenu, setMostrarModificarMenu] = useState(false);
  const [menuSeleccionado, setMenuSeleccionado] = useState(null);
  const [todosMenus, setTodosMenus] = useState([]);

  const handleAñadirMenu = () => {
    setMostrarAñadirMenu(true);
    setHayVentanas(true);
  };

  const closeAñadirMenu = () => {
      setMostrarAñadirMenu(false);
      setHayVentanas(false);
  };

  const handleModificarMenu = (menu) => {
    setMenuSeleccionado(menu);
    setMostrarModificarMenu(true);
    setHayVentanas(true);
  };

  const closeModificarMenu = () => {
    setMostrarModificarMenu(false);
      setHayVentanas(false);
  };

  useEffect(() => {
      const obtenerDatos = async () => {
          const tokenNuevo = Cookies.get('token');
          const permisosNuevos = Cookies.get('permisos');
          setToken(tokenNuevo);
          setPermisos(permisosNuevos);
          await actualizarListaMenus(tokenNuevo);
      };
      obtenerDatos();
  }, [token]);

  const actualizarListaMenus = async (token) => {
    //console.log("Token en actualizar lista:", token)
      const resultado = await listaMenus(token);
      //console.log("Lista menus:", resultado)
      if (resultado.success) {
          const menuActual = resultado.data.find(menu => menu.esActual_menu);
          const otrosMenus = resultado.data.filter(menu => !menu.esActual_menu);
          setMenuActual(menuActual);
          setMenus(otrosMenus);
          if (menuActual) {
            setTodosMenus([menuActual, ...otrosMenus]);
          } else {
            setTodosMenus(otrosMenus);
          }
      } else {
          console.log(resultado ? resultado.message : 'Error desconocido');
      }
  };

  const handleDescargarMenuPDF = (id_menu) => {
      descargarMenuPDF(token, id_menu)
          .then(response => {
              if (response.success) {
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                  link.download = `menu_${id_menu}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
              } else {
                  console.log(response.message);
              }
          })
          .catch(() => {
              console.log('Error al descargar PDF');
          });
  };

  const renderMenuActual = (menu) => {
    const imagenPorDefecto = `${process.env.REACT_APP_URL_IMAGENES}MenusComedor/sinMenu.png`;
      if (!menu) {
        return (
          <div className='row-fluid p-2 d-flex justify-content-between'>
            <div>No hay ningún menú actual.</div>
          </div>
        )
      }

      return (
          <div className="container-fluid ">
              <div className="row-fluid d-flex justify-content-start pb-2">
                <div className='d-flex'>
                  <span className='row-fluid text-black customFontSizeTextoNormal mt-2'>Menú actual:</span>
                  <div className='mt-2'>
                      <span className='text-black customFontSizeTextoNormal ml-2'>{menu.mes_menu}</span>
                  </div>
                  {permisos === "Administrador" &&
                    <img
                      alt="modificar"
                      src={lapizNegro}
                      style={{ height: '2rem', width: '2rem', cursor: 'pointer', marginLeft: '0.3rem', marginTop: '0.5rem' }}
                      onClick={() => handleModificarMenu(menu)}
                    />
                  }
                </div>
              </div>
              <div className="container-fluid p-0" style={{ border: '2px solid black', height: 'calc(100vh - 12rem)', overflow: 'hidden' }}>
                {menu.pdf_menu ? (
                    <iframe src={urlMenus + menu.pdf_menu} style={{ width: '100%', height: '100%' }}></iframe>
                ) : (
                    <img src={imagenPorDefecto} alt="No PDF disponible" style={{ width: '100%', height: '100%' }} />
                )}
              </div>
          </div>
      );
  };

  const renderOtrosMenus = (menus) => {
    return menus.map((menu, index) => (
      <div key={index} className="card mb-3" style={{ backgroundColor: 'white', cursor: 'pointer', padding: '1rem' }}>
          <div className="card-body d-flex justify-content-between align-items-center flex-container">
              <h5 className="card-title customFontSizeTextoMediano text-black text-center">{menu.mes_menu}</h5>
              <div className="d-flex align-items-center">
                  {menu.pdf_menu && (
                    <button onClick={() => handleDescargarMenuPDF(menu.id_menu)} className="btn btn-outline-primary mr-2">Descargar PDF</button>
                  )}
                  {permisos === "Administrador" &&
                      <img
                          alt="modificar"
                          src={lapizNegro}
                          style={{ height: '2rem', width: '2rem', cursor: 'pointer' }}
                          onClick={() => handleModificarMenu(menu)}
                      />
                  }
              </div>
          </div>
      </div>
    ));
  };

  const renderMenusMovil = (menus) => {
    if (!menus || menus.length === 0) {
      return <div>No hay menús disponibles.</div>;
    }
    return (
      <div className="container-fluid p-0" style={{height:'100%'}}>
        {menus.map((menu, index) => (
          <div key={index} className="d-flex flex-column align-items-center p-3" style={{ borderBottom: '1px solid #ccc', width: '100%' }}>
            <div className='d-flex col-12'>
              <div className='col-8'>
                <span className="text-black customFontSizeTextoGrande fw-bold mb-2">{menu.mes_menu}</span>
              </div>
              <div className="d-flex justify-content-center col-4">
                {menu.pdf_menu && (
                  <button onClick={() => handleDescargarMenuPDF(menu.id_menu)} className="btn btn-outline-primary mr-2">Descargar PDF</button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <MetaTags />
      <div className="fondo">
          <Cabecera permisos={permisos}/>
          <div className='d-none d-md-block '>
              <SegundaCabecera hayVentanas={hayVentanas} permisos={permisos} />
              {mostrarAñadirMenu && <VentanaAñadirMenu 
                onClose={closeAñadirMenu} 
                actualizarLista={async () => {
                  await actualizarListaMenus(Cookies.get('token'));
                }}
              />}
              {mostrarModificarMenu && <VentanaModificarMenu
                onClose={closeModificarMenu}
                actualizarLista={async () => {
                  await actualizarListaMenus(Cookies.get('token'));
                }}
                menu={menuSeleccionado}
              />}
              <div className='container-fluid d-flex p-2'>
                  <div className='container-fluid col-8' style={{ height: 'calc(100vh - 9rem)', display: 'grid', opacity: hayVentanas ? 0.4 : 1 }}>
                      {renderMenuActual(menuActual)}
                  </div>
                  <div className='container-fluid col-4' style={{ opacity: hayVentanas ? 0.4 : 1 }}>
                    <div className='row-fluid d-flex justify-content-end p-2'>
                      {permisos === "Administrador" &&
                          <div className=" mt-1 mb-2">
                            <button className="btn" style={{ backgroundColor: '#3C3C3C', border: '1px solid black' }} type="submit" onClick={() => handleAñadirMenu()}>
                              <span className='text-white'>Añadir menú</span>
                            </button>
                          </div>
                      }
                    </div>
                    <div className='container-fluid ' style={{height: 'calc(100vh - 15rem)', overflowY: 'auto', marginTop: permisos === "Administrador" ? '0rem' : '2.5rem'}}>
                      {renderOtrosMenus(menus)}
                    </div>
                  </div>
              </div>
          </div>
          <div className='d-block d-md-none'>
            <div className='container-fluid p-2' style={{ height: 'calc(100vh - 3rem)', overflowY: 'auto', opacity: hayVentanas ? 0.4 : 1 }}>
              {renderMenusMovil(todosMenus)} 
            </div>
          </div>
          {/** Pantalla de overlay para que no se pueda seleccionar ningún objeto o interactuar si hay alguna ventana */}
          {hayVentanas && (
            <div className="overlay" style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2
            }}></div>
          )}
      </div>
    </>
  );
}

export default MenusComedor;